import { useEffect } from 'react';
import {
  getLastServiceWorkerFailureKind,
  reportUnresponsiveServiceWorker,
} from 'services/serviceWorker/diagnostics';
import { logError } from 'setup/logger/logError';
import { logInfo } from 'setup/logger/logInfo';
import { debugLog } from 'shared/logger/debugLog';
import { isEmptyArray } from 'shared/utils/array';
import { createVisibleLoadingClock } from './visibleLoadingClock';

const SLOW_LOADING_MS = 3_000;
/** Long enough that a read (10 s) or a mutation (15 s) has timed out; bulk reads (30 s) may still be waiting. */
const STUCK_LOADING_MS = 15_000;
const ENDLESS_LOADING_MS = 60_000;
const LOADING_CLOCK_TICK_MS = 1_000;

export interface LoadingDurationLogOptions {
  /**
   * Whether a stuck loading is explained by the service worker's diagnostics.
   * Pages that work without the worker (public download links) pass `false`.
   */
  withServiceWorkerDiagnostics?: boolean;
}

interface LoadingThreshold {
  afterVisibleMs: number;
  log: () => void;
}

function isDocumentVisible(): boolean {
  return document.visibilityState === 'visible';
}

function loadingThresholds(reason: string, withServiceWorkerDiagnostics: boolean): LoadingThreshold[] {
  return [
    {
      afterVisibleMs: SLOW_LOADING_MS,
      log: () => debugLog(`SPINNER OVER 3 seconds reason: ${reason}`),
    },
    {
      afterVisibleMs: STUCK_LOADING_MS,
      log: () => {
        logInfo(`SPINNER OVER 15 seconds reason: ${reason}`);
        if (withServiceWorkerDiagnostics) {
          reportUnresponsiveServiceWorker(`spinner: ${reason}`);
        }
      },
    },
    {
      afterVisibleMs: ENDLESS_LOADING_MS,
      log: () =>
        logError(`SPINNER OVER 60 seconds reason: ${reason}`, {
          durationMs: ENDLESS_LOADING_MS,
          ...(withServiceWorkerDiagnostics && {
            serviceWorkerFailureKind: getLastServiceWorkerFailureKind(),
          }),
        }),
    },
  ];
}

/**
 * Logs a loading state seen too long: a debug note after 3 s, a service
 * worker diagnostics snapshot after 15 s and an error after 60 s. Only the
 * time the user sees the loading counts (PT-7330): a tab in the background or
 * a sleeping computer would otherwise fire every threshold at once.
 */
export function useLoadingDurationLog(
  reason: string,
  { withServiceWorkerDiagnostics = true }: LoadingDurationLogOptions = {}
): void {
  useEffect(() => {
    if (!reason) return;
    debugLog(`SPINNER reason: ${reason}`);
    const clock = createVisibleLoadingClock({
      now: Date.now,
      isVisible: isDocumentVisible,
      tickMs: LOADING_CLOCK_TICK_MS,
    });
    let pendingThresholds = loadingThresholds(reason, withServiceWorkerDiagnostics);

    const interval = setInterval(() => {
      const visibleMs = clock.tick();
      const reachedThresholds = pendingThresholds.filter(({ afterVisibleMs }) => afterVisibleMs <= visibleMs);
      pendingThresholds = pendingThresholds.filter(({ afterVisibleMs }) => afterVisibleMs > visibleMs);
      reachedThresholds.forEach(({ log }) => log());
      if (isEmptyArray(pendingThresholds)) {
        clearInterval(interval);
      }
    }, LOADING_CLOCK_TICK_MS);

    return () => {
      clearInterval(interval);
    };
  }, [reason, withServiceWorkerDiagnostics]);
}
